const MINUTE_MS = 60_000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS

/** Fallback locale for call sites with no live locale to hand in (tests, pure-data helpers). */
export const DEFAULT_LOCALE = 'en-US'

/** Anything the UI might hold for a moment in time, including nothing at all. */
export type DateInput = Date | string | number | null | undefined

/** The closed set of date shapes this product renders. */
export type DateStyle =
  | 'weekday'
  | 'month'
  | 'monthDay'
  | 'monthDayYear'
  | 'monthYear'
  | 'weekdayMonthDay'
  | 'fullDay'
  | 'longDate'
  | 'time'

const STYLE_OPTIONS: Record<DateStyle, Intl.DateTimeFormatOptions> = {
  weekday: { weekday: 'short' },
  month: { month: 'short' },
  monthDay: { month: 'short', day: 'numeric' },
  monthDayYear: { day: 'numeric', month: 'short', year: 'numeric' },
  monthYear: { month: 'long', year: 'numeric' },
  weekdayMonthDay: { weekday: 'short', month: 'short', day: 'numeric' },
  fullDay: { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' },
  longDate: { dateStyle: 'long' },
  // 24h in every locale, overriding en's 12h default: the product schedules in one clock.
  time: { hour: '2-digit', minute: '2-digit', hour12: false },
}

const dateFormatterCache = new Map<string, Intl.DateTimeFormat>()

function dateFormatterFor(locale: string, style: DateStyle): Intl.DateTimeFormat {
  const key = `${locale}|${style}`
  let formatter = dateFormatterCache.get(key)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, STYLE_OPTIONS[style])
    dateFormatterCache.set(key, formatter)
  }
  return formatter
}

/** Monday..Sunday, 0-indexed. A fixed UTC week used only to read weekday names out of Intl. */
const WEEKDAY_REFERENCE_MONDAY_UTC = Date.UTC(2024, 0, 1)

const weekdayFormatterCache = new Map<string, Intl.DateTimeFormat>()

/** How wide a weekday label reads: an axis tick, a compact label, or prose. */
export type WeekdayStyle = 'narrow' | 'short' | 'long'

function weekdayFormatterFor(locale: string, style: WeekdayStyle): Intl.DateTimeFormat {
  const key = `${locale}|${style}`
  let formatter = weekdayFormatterCache.get(key)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, { weekday: style, timeZone: 'UTC' })
    weekdayFormatterCache.set(key, formatter)
  }
  return formatter
}

/** The weekday label for `day` (0 = Monday .. 6 = Sunday) at `style`, in `locale`; the index is Monday-first in every locale. */
export function weekdayLabel(locale: string, day: number, style: WeekdayStyle): string {
  const date = new Date(WEEKDAY_REFERENCE_MONDAY_UTC + day * DAY_MS)
  return weekdayFormatterFor(locale, style).format(date)
}

const relativeTimeFormatterCache = new Map<string, Intl.RelativeTimeFormat>()

function relativeTimeFormatterFor(locale: string): Intl.RelativeTimeFormat {
  let formatter = relativeTimeFormatterCache.get(locale)
  if (!formatter) {
    formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
    relativeTimeFormatterCache.set(locale, formatter)
  }
  return formatter
}

function dayKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`
}

/** Whole calendar days from `from` to `to`: positive when `to` is the later day. */
function daysBetween(from: Date, to: Date): number {
  const fromDay = new Date(from.getFullYear(), from.getMonth(), from.getDate())
  const toDay = new Date(to.getFullYear(), to.getMonth(), to.getDate())
  return Math.round((toDay.getTime() - fromDay.getTime()) / DAY_MS)
}

// Hazard: an unstamped server timestamp is `null` and `new Date(null)` is the epoch; parse every value here, not at the call site.
/** Parses any input to a Date, or null when absent or unparseable. Never throws. */
export function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined) {
    return null
  }
  if (typeof value === 'string' && value.trim() === '') {
    return null
  }
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/** Formats `value` in the named style, in `locale`. Returns '' when there is no usable date. Never throws. */
export function formatDate(
  value: DateInput,
  style: DateStyle,
  locale: string = DEFAULT_LOCALE,
): string {
  const date = toDate(value)
  if (!date) {
    return ''
  }
  return dateFormatterFor(locale, style).format(date)
}

/** Relative-time phrasing against `now`, in `locale`, past or future; '' when unusable. Never throws. */
export function formatRelativeTime(
  value: DateInput,
  now: Date,
  locale: string = DEFAULT_LOCALE,
): string {
  const target = toDate(value)
  if (!target) {
    return ''
  }

  const diffMs = target.getTime() - now.getTime()
  const rtf = relativeTimeFormatterFor(locale)

  if (dayKey(target) === dayKey(now)) {
    const diffSeconds = Math.round(diffMs / 1000)
    if (Math.abs(diffSeconds) < 60) {
      return rtf.format(diffSeconds, 'second')
    }
    const diffMinutes = Math.round(diffMs / MINUTE_MS)
    if (Math.abs(diffMinutes) < 60) {
      return rtf.format(diffMinutes, 'minute')
    }
    const diffHours = Math.round(diffMs / HOUR_MS)
    return rtf.format(diffHours, 'hour')
  }

  const dayDiff = daysBetween(now, target)
  if (Math.abs(dayDiff) === 1) {
    return rtf.format(dayDiff, 'day')
  }
  if (Math.abs(dayDiff) < 7) {
    return formatDate(target, 'weekday', locale)
  }

  return target.getFullYear() === now.getFullYear()
    ? formatDate(target, 'monthDay', locale)
    : formatDate(target, 'monthDayYear', locale)
}

/** A calendar-day group label against `now`: "today", "yesterday", else an absolute date; '' when unusable. Never throws. */
export function formatDayLabel(
  value: DateInput,
  now: Date,
  locale: string = DEFAULT_LOCALE,
): string {
  const target = toDate(value)
  if (!target) {
    return ''
  }

  const dayDiff = daysBetween(now, target)
  if (dayDiff === 0 || dayDiff === -1) {
    return relativeTimeFormatterFor(locale).format(dayDiff, 'day')
  }

  return formatDate(target, 'monthDayYear', locale)
}
